import type { QueryClient } from '@tanstack/react-query';
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import { Skeleton } from '../components/ui/skeleton';
import { Toaster } from '../components/ui/sonner';
import { useTheme } from '../hooks/use-theme';
import { useAuthStore } from '../stores/auth.store';

interface RouterContext {
  queryClient: QueryClient;
  permissions: {
    can: (key: string) => boolean;
    user: { id: string; email: string; name: string } | null;
  };
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async () => {
    const { user, setSession, clearSession } = useAuthStore.getState();
    if (user) return;
    try {
      const res = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error('no session');
      const data = (await res.json()) as {
        accessToken: string;
        user: {
          id: string;
          email: string;
          name: string;
          avatarUrl?: string | null;
          permissions: string[];
          licensingAuthorities?: string[];
        };
      };
      setSession(
        {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name,
          avatarUrl: data.user.avatarUrl,
        },
        data.user.permissions,
        data.accessToken,
        false,
        data.user.licensingAuthorities ?? [],
      );

      // Restore impersonation session after refresh (dev/QA only)
      const impersonatedUserId = sessionStorage.getItem('impersonated-user-id');
      if (impersonatedUserId && data.user.permissions.includes('user:impersonate')) {
        try {
          const impRes = await fetch(`/api/v1/auth/impersonate/${impersonatedUserId}`, {
            method: 'POST',
            credentials: 'include',
            headers: { Authorization: `Bearer ${data.accessToken}` },
          });
          if (impRes.ok) {
            const impData = (await impRes.json()) as {
              accessToken: string;
              user: {
                id: string;
                email: string;
                name: string;
                permissions: string[];
                licensingAuthorities: string[];
              };
            };
            useAuthStore
              .getState()
              .startImpersonation(
                { id: impData.user.id, email: impData.user.email, name: impData.user.name },
                impData.user.permissions,
                impData.accessToken,
                impData.user.licensingAuthorities,
              );
          } else {
            sessionStorage.removeItem('impersonated-user-id');
          }
        } catch {
          sessionStorage.removeItem('impersonated-user-id');
        }
      }
    } catch {
      clearSession();
    }
  },
  component: RootLayout,
});

function RootLayout() {
  const { isLoading } = useAuthStore();
  useTheme();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f4f6f9]">
        <div className="space-y-4 w-full max-w-sm p-8">
          <Skeleton className="h-8 w-32 mx-auto" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-3/5" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Outlet />
      <Toaster />
    </>
  );
}
